/**
 * Keep focused page fields visible above the keyboard.
 *
 * Storefronts lock `body.store-shell` (`overflow: hidden`) and scroll
 * `.store-site` instead of the window. iOS Safari tries to pan the document /
 * visual viewport, which the shared tracker then undoes
 * (`stabilizeStoreLayoutViewport`), so a field in the canvas can stay behind
 * the keyboard unless this module scrolls the inner scroller.
 *
 * The owner dock / AI composer already track `--store-kb-inset`. This module
 * never scrolls or pads for those — a second compensation there is the
 * "composer jumps twice" bug.
 *
 * Viewport sizes come from the shared tracker. There is no settle timeout:
 * each visualViewport frame is enough to keep the field in the visible band.
 */
import {
  getStoreVisualViewport,
  onStoreVisualViewport,
  startStoreVisualViewport,
} from '~/utils/storeVisualViewport'

const SKIP_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
])

const SKIP_CHROME = '.store-end-fabs, .store-owner-ai-prompt, .store-ai-coach, .store-trainer, .sai-composer, .ss-overlay, .app-modal, .iet-edit-sheet, [role="dialog"]'

const MIN_SCROLL_DELTA = 4
const BOTTOM_GAP = 16
const TOP_GAP = 12

type PadLock = {
  el: HTMLElement
  inline: string
  computed: number
}

let started = false
let unsubViewport: (() => void) | null = null
let focused: HTMLElement | null = null
let raf = 0
let revealing = false
let padLock: PadLock | null = null

function isNativeTextControl(el: HTMLElement): boolean {
  if (el instanceof HTMLTextAreaElement) return !el.disabled && !el.readOnly
  if (el instanceof HTMLSelectElement) return !el.disabled
  if (el instanceof HTMLInputElement) {
    if (el.disabled || el.readOnly) return false
    const type = (el.type || 'text').toLowerCase()
    return !SKIP_INPUT_TYPES.has(type)
  }
  return false
}

/**
 * Resolve the actual editing host.
 *
 * `editable` in this app is a Vue prop (InlineEditableText / PdpEditable), not
 * a DOM attribute. While editing it becomes `contenteditable="plaintext-only"`
 * (or `"true"` on StoreRichTextField). `isContentEditable` covers both, plus
 * nested nodes inside a contenteditable host.
 */
export function resolveEditableHost(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null
  if (isNativeTextControl(target)) return target
  if (target.isContentEditable) return target
  const host = target.closest('[contenteditable]:not([contenteditable="false"])')
  if (host instanceof HTMLElement && host.isContentEditable) return host
  return null
}

function isFixedChromeHost(host: HTMLElement): boolean {
  return Boolean(host.closest(SKIP_CHROME))
}

function visualViewportBounds(): { top: number, bottom: number, height: number } | null {
  const snap = getStoreVisualViewport()
  const vv = window.visualViewport
  if (!vv) {
    return {
      top: 0,
      bottom: window.innerHeight,
      height: window.innerHeight,
    }
  }
  if (snap.visualHeight < 80) return null
  return {
    top: snap.offsetTop,
    bottom: snap.offsetTop + snap.visualHeight,
    height: snap.visualHeight,
  }
}

function isVerticallyScrollable(node: HTMLElement): boolean {
  const style = window.getComputedStyle(node)
  const overflowY = style.overflowY
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false
  return node.scrollHeight > node.clientHeight + 1
}

function documentScroller(): HTMLElement | null {
  if (document.body.classList.contains('store-shell')) return null
  const el = document.scrollingElement
  return el instanceof HTMLElement ? el : document.documentElement
}

function scrollParents(el: HTMLElement): HTMLElement[] {
  const parents: HTMLElement[] = []
  let node: HTMLElement | null = el.parentElement
  while (node && node !== document.documentElement) {
    if (isVerticallyScrollable(node)) parents.push(node)
    node = node.parentElement
  }
  const doc = documentScroller()
  if (doc && !parents.includes(doc) && isVerticallyScrollable(doc)) parents.push(doc)
  return parents
}

function padTargetFor(el: HTMLElement, parents: HTMLElement[]): HTMLElement {
  if (parents.length) return parents[parents.length - 1]!
  const site = el.closest('.store-site')
  if (site instanceof HTMLElement) return site
  const main = el.closest('.app-main')
  if (main instanceof HTMLElement) return main
  return documentScroller() || document.body
}

function topChromeInset(el: HTMLElement): number {
  const overlay = el.closest('.ss-overlay, .app-modal, [role="dialog"]')
  if (overlay) return 0
  const site = el.closest('.store-site')
  if (site instanceof HTMLElement) {
    const cs = getComputedStyle(site)
    const nav = parseFloat(cs.getPropertyValue('--st-chrome-nav-h')) || 0
    return nav
  }
  if (el.closest('.app-shell')) {
    return parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--app-header-h'),
    ) || 0
  }
  return 0
}

function bottomChromeInset(el: HTMLElement): number {
  if (el.closest('.ss-overlay, .app-modal, [role="dialog"]')) return 0
  const box = parseFloat(
    getComputedStyle(document.body).getPropertyValue('--store-owner-dock-box-h'),
  ) || 0
  return box
}

function caretRect(host: HTMLElement): DOMRect | null {
  if (!host.isContentEditable) return null
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0)
  if (!host.contains(range.commonAncestorContainer)) return null
  const rects = range.getClientRects()
  const rect = rects.length ? rects[0] : range.getBoundingClientRect()
  if (!rect || (rect.width === 0 && rect.height === 0 && rect.top === 0)) return null
  return rect
}

function revealRect(host: HTMLElement, visibleHeight: number): { top: number, bottom: number } | null {
  const box = host.getBoundingClientRect()
  if (!box.width && !box.height) return null
  const caret = caretRect(host)
  if (caret && caret.bottom >= caret.top) {
    return { top: caret.top, bottom: Math.max(caret.bottom, caret.top + 18) }
  }
  const budget = Math.max(48, visibleHeight - TOP_GAP - BOTTOM_GAP)
  if (box.height <= budget) return { top: box.top, bottom: box.bottom }
  return { top: box.bottom - 48, bottom: box.bottom }
}

function restorePad() {
  if (!padLock) return
  padLock.el.style.paddingBottom = padLock.inline
  padLock = null
}

function applyPad(el: HTMLElement, extra: number) {
  if (extra <= 1) return
  if (padLock && padLock.el !== el) restorePad()
  if (!padLock) {
    padLock = {
      el,
      inline: el.style.paddingBottom,
      computed: parseFloat(getComputedStyle(el).paddingBottom) || 0,
    }
  }
  const next = `${padLock.computed + extra}px`
  if (el.style.paddingBottom !== next) el.style.paddingBottom = next
}

function scrollByDelta(parents: HTMLElement[], delta: number): number {
  let remaining = delta
  for (const parent of parents) {
    if (Math.abs(remaining) < 1) break
    const max = Math.max(0, parent.scrollHeight - parent.clientHeight)
    const before = parent.scrollTop
    const next = Math.min(max, Math.max(0, before + remaining))
    if (next !== before) parent.scrollTop = next
    remaining -= parent.scrollTop - before
  }
  return remaining
}

function revealFocused() {
  if (revealing) return
  const host = focused
  if (!host || !host.isConnected) {
    restorePad()
    return
  }
  if (isFixedChromeHost(host)) {
    restorePad()
    return
  }
  if (getStoreVisualViewport().keyboardInset <= 0) {
    restorePad()
    return
  }
  const visible = visualViewportBounds()
  if (!visible) return

  revealing = true
  try {
    const visibleTop = visible.top + TOP_GAP + topChromeInset(host)
    const visibleBottom = visible.bottom - BOTTOM_GAP - bottomChromeInset(host)
    if (visibleBottom <= visibleTop + 24) return

    const rect = revealRect(host, visible.height)
    if (!rect) return

    let delta = 0
    if (rect.bottom > visibleBottom) delta = rect.bottom - visibleBottom
    else if (rect.top < visibleTop) delta = rect.top - visibleTop
    if (Math.abs(delta) < MIN_SCROLL_DELTA) return

    const parents = scrollParents(host)
    const padEl = padTargetFor(host, parents)

    if (delta > 0) {
      let room = 0
      const chain = parents.includes(padEl) ? parents : [...parents, padEl]
      for (const parent of chain) {
        room += Math.max(0, parent.scrollHeight - parent.clientHeight - parent.scrollTop)
      }
      applyPad(padEl, Math.ceil(Math.max(0, delta - room)))
    }
    else {
      restorePad()
    }

    scrollByDelta(scrollParents(host), delta)
  }
  finally {
    revealing = false
  }
}

function scheduleReveal() {
  if (!focused) return
  if (raf) return
  raf = window.requestAnimationFrame(() => {
    raf = 0
    revealFocused()
  })
}

function clearFocus() {
  focused = null
  if (raf) {
    window.cancelAnimationFrame(raf)
    raf = 0
  }
  restorePad()
}

function onFocusIn(event: FocusEvent) {
  const host = resolveEditableHost(event.target)
  if (!host) return
  focused = host
  scheduleReveal()
}

function onFocusOut() {
  window.requestAnimationFrame(() => {
    const active = resolveEditableHost(document.activeElement)
    if (active) {
      focused = active
      return
    }
    clearFocus()
  })
}

function onInput(event: Event) {
  if (!focused) return
  if (!(event.target instanceof Node)) return
  if (event.target !== focused && !focused.contains(event.target)) return
  scheduleReveal()
}

export function startKeyboardAwareScroll() {
  if (started || typeof window === 'undefined') return
  started = true
  startStoreVisualViewport()
  document.addEventListener('focusin', onFocusIn, { capture: true })
  document.addEventListener('focusout', onFocusOut, { capture: true })
  document.addEventListener('input', onInput, { capture: true })
  unsubViewport = onStoreVisualViewport(() => {
    if (focused) scheduleReveal()
  })
  window.addEventListener('pagehide', stopKeyboardAwareScroll, { once: true })
}

export function stopKeyboardAwareScroll() {
  if (!started) return
  started = false
  document.removeEventListener('focusin', onFocusIn, true)
  document.removeEventListener('focusout', onFocusOut, true)
  document.removeEventListener('input', onInput, true)
  unsubViewport?.()
  unsubViewport = null
  clearFocus()
}
